import React from 'react';
import CheckboxCell from '../../../AppTable/cells/CheckboxCell/CheckboxCell';
import Engagement from '../table-parts/Engagement/Engagement';
import LastModified from '../table-parts/LastModified/LastModified';
import Schedule from '../table-parts/Schedule/Schedule';
import AudiencesCell, { NotificationFullAudiences } from '../table-parts/AudiencesCell/AudiencesCell';
import DataCell from '../../../AppTable/cells/DataCell/DataCell';
import { Notification } from '../../../../models';
import { AudienceFilter } from '../table-parts/Segment/AudienceFilter';
import { BY_SEGMENTS } from './consts';
import { NotificationTitle } from '../table-parts/NotificationTitle/NotificationTitle';
import { WMTableColumnShape } from '../../../AppTable/WMTable/WMTable';
import { END_USERS_RECIPIENTS_KEY, ALL_RECIPIENTS_KEY, INotification } from '@communication-center/data-objects';

export const API_CELL_TEXT = '⚙️ API';

export const TABLE_COLUMNS: Record<string, WMTableColumnShape<INotification>> = {
    CHECKED: {
        title: '',
        key: 'checked',
        dataKey: 'checked',
        sortable: false,
        width: 60,
        minWidth: 60,
        flexGrow: 0,
        cellRenderer: ({ rowData }) => <CheckboxCell rowData={rowData} />
    },
    NAME: {
        title: 'Name',
        key: 'title',
        dataKey: 'title',
        sortable: true,
        minWidth: 148,
        flexGrow: 2,
        cellRenderer: ({ rowData }) => <NotificationTitle notification={rowData} />
    },
    AUDIENCES: {
        title: 'Audience',
        key: 'audiencesIds',
        dataKey: 'audiencesIds',
        sortable: true,
        renderFilterItem: filter => <AudienceFilter filter={filter} />,
        minWidth: 125,
        filterMetaData: {
            active: true,
            hideSearch: false,
            execFilter: (item: NotificationFullAudiences, checkedList: string[]) => {
                return (
                    (checkedList.includes(END_USERS_RECIPIENTS_KEY) && !item.audiences?.length && !!item?.endUsersIds?.length) ||
                    (checkedList.includes(BY_SEGMENTS) && !item.audiences?.length && !!item?.targetAudience?.length) ||
                    (checkedList.includes(ALL_RECIPIENTS_KEY) && !item.audiences?.length) ||
                    item.audiences?.some?.(activeSegment => checkedList.includes(activeSegment.label))
                );
            },
            extractFilterValues: (item: NotificationFullAudiences) => {
                const nestedAudienceArr: string[] = item?.audiences?.map(ta => ta.label) || [];

                if (!item?.audiences?.length) {
                    if (item?.targetAudience?.length) {
                        nestedAudienceArr.push(BY_SEGMENTS);
                    } else if (item.endUsersIds?.length) {
                        nestedAudienceArr.push(END_USERS_RECIPIENTS_KEY);
                    } else {
                        nestedAudienceArr.push(ALL_RECIPIENTS_KEY);
                    }
                }

                return nestedAudienceArr;
            }
        },
        sortFilterValues: (a: $TSFixMe, b: $TSFixMe) => {
            if (a === END_USERS_RECIPIENTS_KEY && b !== END_USERS_RECIPIENTS_KEY) {
                return -1;
            }

            if (a === ALL_RECIPIENTS_KEY && b === BY_SEGMENTS) {
                return -1;
            }

            if (a === BY_SEGMENTS && b === ALL_RECIPIENTS_KEY) {
                return 1;
            }

            if (a === ALL_RECIPIENTS_KEY) {
                return -1;
            }
            if (b === ALL_RECIPIENTS_KEY) {
                return 1;
            }

            if (a === undefined || b === undefined) return 0;
            if (a >= b) return 1;
            if (a < b) return -1;
            return 0;
        },
        cellRenderer: ({ rowData }: { rowData: INotification }) => <AudiencesCell rowData={rowData as NotificationFullAudiences} />
    },
    CREATOR: {
        title: 'Creator',
        key: 'user.label',
        dataKey: 'user.label',
        sortable: true,
        minWidth: 100,
        skeleton: {
            width: 30,
            height: 12
        },
        filterMetaData: {
            active: true,
            hideSearch: false,
            extractFilterValues: (item: Notification) => (item?.user?.isPublicApiUser ? API_CELL_TEXT : item?.user?.label),
            execFilter: (item: Notification, checkedList: string[]) =>
                (checkedList.includes(API_CELL_TEXT) && item?.user?.isPublicApiUser) || checkedList?.includes?.(item?.user?.label as string)
        },
        cellRenderer: ({ rowData }: { rowData: Notification }) => (
            <DataCell
                data={(rowData?.user?.isPublicApiUser ? API_CELL_TEXT : rowData?.user?.label) || ''}
                id={rowData.id!}
                testKey="creator"
                capitalize
            />
        )
    },
    SCHEDULE: {
        title: 'Start Date',
        key: 'publishStates:startAt',
        dataKey: 'publishStates:startAt',
        sortable: true,
        minWidth: 100,
        cellRenderer: ({ rowData }) => <Schedule rowData={rowData} />
    },
    CLICKS: {
        title: 'Clicks',
        key: 'engagement',
        dataKey: 'engagement',
        sortable: true,
        className: 'WMTable-cell--center',
        minWidth: 100,
        cellRenderer: ({ rowData }) => <Engagement rowData={rowData} />
    },
    LAST_MODIFIED: {
        title: 'Last Modified',
        key: 'updatedAt',
        dataKey: 'updatedAt',
        sortable: true,
        minWidth: 100,
        cellRenderer: ({ rowData }) => <LastModified rowData={rowData} />
    }
};
